import type { AgenticLogicView } from "@src/features/ai/pages/agentic-logic/surface/ruleViewParams";
import { browserSessionStorage } from "@src/utils/browserSessionStorage";

/**
 * Hiding the Atlas rail on an existing rule (ENG-7644): the rail's minus hides
 * it, the work area takes the full width, and a toolbar button brings it back
 * with the same conversation. Hiding is a frame change only — the ONE chat
 * instance stays mounted (see `AppLayoutShell`), exactly like the full-screen
 * switch.
 *
 * Hiding belongs to a standalone docked rail. During a new-rule build the
 * toolbar keeps a way to reopen Atlas while its work continues. It does not
 * apply while an existing rule is being edited:
 *   - editing: an Atlas session working on the rule (a stored entry context),
 *     or unsaved edits on the rule's page (`ruleEditing`).
 * While either holds the minus is withheld (the ⋮ stays). A rail hidden before
 * an Atlas build or session started is shown again, because the conversation
 * it is running is the one thing the merchant must be able to see; unsaved
 * edits do NOT reopen it — typing in the editor must not pop a rail open.
 */
export interface AtlasRailCollapseInput {
  surfaceKind: "standalone" | "embedded" | null;
  view: AgenticLogicView;
  layout: "full" | "docked";
  /** An Atlas session is running (a stored entry context). */
  sessionActive: boolean;
  buildingRuleId: string | null;
  /** The rule's page holds unsaved edits. */
  ruleEditing: boolean;
  /** The merchant's choice, remembered for the browser session. */
  collapsed: boolean;
}

export interface AtlasRailCollapse {
  /** Whether the rail header offers the minus that hides it. */
  canHide: boolean;
  /** Whether the rail is hidden now (and the toolbar offers Atlas instead). */
  hidden: boolean;
}

export const resolveAtlasRailCollapse = ({
  surfaceKind,
  view,
  layout,
  sessionActive,
  buildingRuleId,
  ruleEditing,
  collapsed,
}: AtlasRailCollapseInput): AtlasRailCollapse => {
  const onHideableView =
    surfaceKind === "standalone" && view.kind === "rule" && layout === "docked";
  const creatingRule =
    surfaceKind === "standalone" &&
    layout === "docked" &&
    sessionActive &&
    (view.kind === "new" ||
      (view.kind === "rule" && buildingRuleId === view.ruleId));
  const atlasAtWork = sessionActive || buildingRuleId !== null;
  return {
    canHide: (creatingRule || (onHideableView && !atlasAtWork)) && !ruleEditing,
    hidden: (creatingRule || (onHideableView && !atlasAtWork)) && collapsed,
  };
};

const STORAGE_KEY = "ig:atlas-rail-collapsed";

/**
 * The remembered choice, for the store's initial state. Kept for the browser
 * session (`sessionStorage`): a new tab starts with the rail open. Storage
 * that is unavailable or holds anything else reads as "open".
 */
export const readStoredAtlasRailCollapsed = (
  storage: Storage | null = browserSessionStorage()
): boolean => {
  try {
    return storage?.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const storeAtlasRailCollapsed = (
  collapsed: boolean,
  storage: Storage | null = browserSessionStorage()
) => {
  try {
    if (collapsed) {
      storage?.setItem(STORAGE_KEY, "true");
    } else {
      storage?.removeItem(STORAGE_KEY);
    }
  } catch {
    // A rail that forgets it was hidden only reopens on the next load.
  }
};
